import { useState, useEffect, useRef } from 'react';
import { fetchGameDetails } from '../lib/api';
import { mockGames } from '../data/mockGames';
import type { Game } from '../types';

export function useGame(id: number) {
  const [game, setGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let isMounted = true;

    const fetchData = async () => {
      try {
        const data = await fetchGameDetails(id);

        if (!isMounted || controller.signal.aborted) return;

        if (!data || typeof data !== 'object') {
          throw new Error('Invalid game data received');
        }

        setGame(data);
        setError(null);
      } catch (err) {
        if (!isMounted || controller.signal.aborted) return;
        console.error('Error fetching game details:', err);
        setError('Failed to load game details');
        
        // Fallback to mock data
        const mockGame = mockGames.find(g => g.id === id);
        setGame(mockGame || null);
      } finally {
        if (isMounted && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id]);

  return { game, isLoading, error };
}